export const calculateProfit = (purchase: number, sell: number): number => {
	if (purchase === 0) return 0;
	return parseFloat((((sell - purchase) / purchase) * 100).toFixed(2));
};

/**
 * Get Today's date string in ISO format (UTC)
 * @returns UTC date string in ISO format
 */
export const getTodayUTCDateString = (): string => {
	const now = new Date().toISOString();
	return now;
};

/**
 * Convert UTC date string to IST date string
 * @param utcDateISOString UTC date string in ISO format
 * @returns IST date string in ISO format
 */
export const convertUTCToIST = (utcDateISOString: string): string => {
	// Parse the UTC date string into a Date object
	const utcDate = new Date(utcDateISOString);

	// IST is UTC + 5 hours 30 minutes
	const istOffset = 5.5 * 60 * 60 * 1000; // in milliseconds

	// Add the offset to the UTC time to get the time in IST
	const istDate = new Date(utcDate.getTime() + istOffset);

	// Format the IST date into an ISO-like string using UTC methods.
	// Using UTC methods on the 'istDate' effectively treats it as a UTC date,
	// which aligns with the manual offset and prevents timezone-related issues.
	const yyyy = istDate.getUTCFullYear();
	const mm = String(istDate.getUTCMonth() + 1).padStart(2, "0");
	const dd = String(istDate.getUTCDate()).padStart(2, "0");
	const hh = String(istDate.getUTCHours()).padStart(2, "0");
	const min = String(istDate.getUTCMinutes()).padStart(2, "0");
	const ss = String(istDate.getUTCSeconds()).padStart(2, "0");

	return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}+05:30`;
};

/**
 * Convert IST date string to UTC date string
 * @param istDateISOString IST date string in ISO format
 * @returns UTC date string in ISO format
 */
export const convertISTToUTC = (istDateISOString: string): string => {
	// Parse the IST date string into a Date object
	const istDate = new Date(istDateISOString);

	// IST is UTC + 5 hours 30 minutes
	const utcOffset = 5.5 * 60 * 60 * 1000; // in milliseconds

	// Subtract the offset from the IST time to get the time in UTC
	const utcDate = new Date(istDate.getTime() - utcOffset);
	return utcDate.toISOString();
};

/**
 * Format UTC date string to DD-MM-YYYY in IST timezone
 * @param utcDateISOString UTC date string in ISO format
 * @returns Formatted date string in IST DD-MM-YYYY format
 */
export const formatDate = (
	utcDateISOString: string | undefined | null
): string => {
	const value = String(utcDateISOString || "").trim();
	if (!value) return "";

	let utcDate: Date;
	if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
		utcDate = new Date(`${value}T00:00:00.000Z`);
	} else {
		utcDate = new Date(value);
	}

	if (Number.isNaN(utcDate.getTime())) {
		// Fallback to date-only extraction for non-standard strings
		const dateOnlyMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
		if (dateOnlyMatch) {
			const [dateOnly] = dateOnlyMatch;
			return dateOnly.split("-").reverse().join("-");
		}
		return "";
	}

	// IST is UTC + 5 hours 30 minutes
	const istOffset = 5.5 * 60 * 60 * 1000; // in milliseconds

	// Add the offset to the UTC time to get the time in IST
	const istDate = new Date(utcDate.getTime() + istOffset);

	// Format the IST date into an ISO-like string using UTC methods.
	// Using UTC methods on the 'istDate' effectively treats it as a UTC date,
	// which aligns with the manual offset and prevents timezone-related issues.
	const yyyy = istDate.getUTCFullYear();
	const mm = String(istDate.getUTCMonth() + 1).padStart(2, "0");
	const dd = String(istDate.getUTCDate()).padStart(2, "0");

	return `${dd}-${mm}-${yyyy}`;
};
